import { Box, Button, Container, Grid, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import useClientContext from "../../../context/useClientContext";
import CorporateAccountingIcon from "../../common/domain-icons/CorporateAccountingIcon";
import FundOperationsIcon from "../../common/domain-icons/FundOperationsIcon";
import FundraisingIcon from "../../common/domain-icons/FundraisingIcon";
import InvestorPortalIcon from "../../common/domain-icons/InvestorPortalIcon";
import PortfolioMonitoringAppIcon from "../../common/domain-icons/PortfolioMonitoringAppIcon";
import SmartViewsIcon from "./SmartViewsIcon";
import { pageRoutes } from "../../../routes";
import { getBusinessCentralRedirectUrl } from "../../../utilities/navigationHelper";
import GeneralPageHeader from "../../common/GeneralPageHeader";
import InvestorPortalImpersonateButton from "../investor-portal/InvestorPortalImpersonateButton";
import AppCard from "./AppCard";

const ManagerHomePage = () => {
  const {
    clientCode,
    title: clientTitle,
    businessCentralApp,
    investorPortalApp,
    reportingPortalApp,
    fundraisingApp,
    portfolioMonitoringApp,
    hasAnyPermission,
    settings,
  } = useClientContext();

  const hasAccessToFundOps = hasAnyPermission(["ViewFundOpsLink"]);
  const hasAccessToCorpAccounting = hasAnyPermission(["ViewCorpAccountingLink"]);
  const hasAccessToReportingPortal = hasAnyPermission(["ManageReports", "ViewReports"]);
  const canInspectInvestorPortal = hasAnyPermission(["ViewInvestorPortalOnBehalfOfInvestor"]);
  const hasAccessToFundraising = hasAnyPermission(["ViewFundraisings", "ManageFundraisings"]);
  const hasAccessToPortfolioMonitoring = hasAnyPermission(["ViewPortfolioMonitoring", "ManagePortfolioMonitoring"]);

  const isDataCollectionEnabled = Boolean(portfolioMonitoringApp?.dataCollection?.enabled);

  const fundOpsUrl = settings.businessCentralSettings
    ? getBusinessCentralRedirectUrl(clientCode, settings.businessCentralSettings, "EAC Fund Accountant")
    : undefined;

  const corpAccountingUrl = settings.businessCentralSettings
    ? getBusinessCentralRedirectUrl(clientCode, settings.businessCentralSettings, "EAC Corporate Accountant")
    : undefined;

  return (
    <>
      <GeneralPageHeader title="Home" />
      <Container maxWidth={false}>
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>{`Welcome to ${clientTitle} Admin Portal`}</Typography>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Apps
          </Typography>
          <Grid container spacing={3}>
            {businessCentralApp?.fundAccountingEnabled && hasAccessToFundOps && fundOpsUrl && (
              <Grid>
                <AppCard title="Fund Operations" Icon={FundOperationsIcon}>
                  <Button
                    component={Link}
                    href={fundOpsUrl}
                    color="secondary"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ minWidth: "unset" }}
                  >
                    Open
                  </Button>
                </AppCard>
              </Grid>
            )}

            {businessCentralApp?.corporateAccountingEnabled && hasAccessToCorpAccounting && corpAccountingUrl && (
              <Grid>
                <AppCard title="Corporate Accounting" Icon={CorporateAccountingIcon}>
                  <Button
                    component={Link}
                    href={corpAccountingUrl}
                    color="secondary"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ minWidth: "unset" }}
                  >
                    Open
                  </Button>
                </AppCard>
              </Grid>
            )}

            {reportingPortalApp && hasAccessToReportingPortal && (
              <Grid>
                <AppCard title="SmartViews" Icon={SmartViewsIcon}>
                  <Button
                    component={RouterLink}
                    to={`/${clientCode}/${pageRoutes.smartViews}/${pageRoutes.companyReports}`}
                    color="secondary"
                    sx={{ minWidth: "unset" }}
                  >
                    Open
                  </Button>
                </AppCard>
              </Grid>
            )}

            {canInspectInvestorPortal && investorPortalApp && (
              <Grid>
                <AppCard title="Investor Portal" Icon={InvestorPortalIcon}>
                  <InvestorPortalImpersonateButton variant="text" color="secondary" sx={{ minWidth: "unset" }} />
                </AppCard>
              </Grid>
            )}

            {hasAccessToFundraising && fundraisingApp && (
              <Grid>
                <AppCard title="Fundraising" Icon={FundraisingIcon}>
                  <Button
                    component={RouterLink}
                    to={`/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.fundraisings}`}
                    color="secondary"
                    sx={{ minWidth: "unset" }}
                  >
                    Open
                  </Button>
                </AppCard>
              </Grid>
            )}

            {hasAccessToPortfolioMonitoring && isDataCollectionEnabled && (
              <Grid>
                <AppCard title="Portfolio Monitoring" Icon={PortfolioMonitoringAppIcon}>
                  <Button
                    component={RouterLink}
                    to={`/${clientCode}/${pageRoutes.portfolio}/${pageRoutes.dataRequests}`}
                    color="secondary"
                    sx={{ minWidth: "unset" }}
                  >
                    Open
                  </Button>
                </AppCard>
              </Grid>
            )}
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default ManagerHomePage;
